import * as THREE from "three";

/**
 * Zelda-style breadcrumb trail: a smoothed strip of small glowing pills
 * hugging the maze floor, with a directional pulse that chases along the
 * path. The pulse is a real geometric ripple - each pill is subdivided
 * along its length so the vertex shader can arch it upward as the pulse
 * passes through, rather than faking motion with a rigid tilt.
 *
 * The destination itself is a separate, static ring decal - not a pill,
 * and it never bends/pulses - so the endpoint reads as "you're here"
 * rather than another animated trail segment.
 *
 * Consumers only ever call setPath()/update()/clear() - all the spline
 * fitting, instancing and shader plumbing lives in here so gameScreen.js
 * just deals in "here is the sequence of walk directions" (unit vectors
 * on the maze sphere, start -> target).
 */

const MAX_PILLS = 220;

const PILL_SPACING = 0.16; // world units between pill centers along the smoothed path
const PILL_HALF_LENGTH = 0.05; // half-length of the pill's straight section
const PILL_RADIUS = 0.032; // cap radius == half-width
const GLOW_PADDING = 0.02; // extra plane margin so the soft halo isn't clipped - kept tight so neighbouring pill quads barely overlap
const SURFACE_OFFSET = 0.026; // lift above the ground to avoid z-fighting
const LENGTH_SEGMENTS = 16; // subdivision along the pill's length, for a smooth arch

const PULSE_SPEED = 0.4; // path-fraction per second the pulse travels start -> target
const PULSE_WIDTH = 0.1; // gaussian width of the pulse, in path-fraction units
const PULSE_CYCLE = 1.7; // >1 so there's a gap between one pulse finishing and the next starting
const BEND_HEIGHT = 0.032; // how far a pill arches off the floor at the peak of the pulse

const RING_OUTER_RADIUS = 0.078; // ring band spans roughly 0.6x-1.0x of this, see ringFragmentShader
const RING_SPIN_SPEED = 0.6; // radians/sec - just enough to read as "alive", not a bounce

const BASE_COLOR = new THREE.Color(0x6ee7ff);
const TARGET_COLOR = new THREE.Color(0xffd166);

const _abTmp = new THREE.Vector3();
const _apTmp = new THREE.Vector3();
const _closestTmp = new THREE.Vector3();

function sqDistToSegment(p, a, b) {
  _abTmp.subVectors(b, a);
  const lenSq = _abTmp.lengthSq();
  const t = lenSq > 1e-9 ? THREE.MathUtils.clamp(_apTmp.subVectors(p, a).dot(_abTmp) / lenSq, 0, 1) : 0;
  _closestTmp.copy(a).addScaledVector(_abTmp, t);
  return _closestTmp.distanceToSquared(p);
}

/** Ramer-Douglas-Peucker: collapses the grid-stairstep points along straight
 * runs down to their endpoints, while keeping genuine corners intact - this
 * is what lets the corners come out as one clean bend instead of stair-steps. */
function simplifyPolyline(points, epsilon) {
  if (points.length < 3) return points.slice();
  const sqEpsilon = epsilon * epsilon;
  const keep = new Uint8Array(points.length);
  keep[0] = 1;
  keep[points.length - 1] = 1;
  const stack = [[0, points.length - 1]];
  while (stack.length) {
    const [start, end] = stack.pop();
    let maxDist = 0, maxIndex = -1;
    for (let i = start + 1; i < end; i++) {
      const d = sqDistToSegment(points[i], points[start], points[end]);
      if (d > maxDist) { maxDist = d; maxIndex = i; }
    }
    if (maxIndex !== -1 && maxDist > sqEpsilon) {
      keep[maxIndex] = 1;
      stack.push([start, maxIndex], [maxIndex, end]);
    }
  }
  return points.filter((_, i) => keep[i] === 1);
}

const pillVertexShader = /* glsl */ `
  attribute float aT;
  attribute float aSeed;

  uniform float uPulsePhase;
  uniform float uPulseWidth;
  uniform float uHalfSpan;
  uniform float uBendHeight;

  varying vec2 vLocalPos;
  varying float vIntensity;
  varying float vSeed;

  void main() {
    vLocalPos = position.xy;
    vSeed = aSeed;

    float dt = aT - uPulsePhase;
    float intensity = exp(-(dt * dt) / (2.0 * uPulseWidth * uPulseWidth));
    vIntensity = intensity;

    float archX = clamp(position.x / uHalfSpan, -1.0, 1.0);
    float arch = cos(archX * 1.5707963);
    vec3 displaced = position;
    displaced.z += uBendHeight * intensity * arch;

    #ifdef USE_INSTANCING
      vec4 worldPos = instanceMatrix * vec4(displaced, 1.0);
    #else
      vec4 worldPos = vec4(displaced, 1.0);
    #endif
    gl_Position = projectionMatrix * modelViewMatrix * worldPos;
  }
`;

const pillFragmentShader = /* glsl */ `
  uniform float uTime;
  uniform float uHalfLength;
  uniform float uRadius;
  uniform float uBaseOpacity;
  uniform float uGlowOpacity;
  uniform vec3 uColor;

  varying vec2 vLocalPos;
  varying float vIntensity;
  varying float vSeed;

  float sdCapsuleH(vec2 p, float halfLength, float radius) {
    p.x = max(abs(p.x) - halfLength, 0.0);
    return length(p) - radius;
  }

  void main() {
    float d = sdCapsuleH(vLocalPos, uHalfLength, uRadius);
    float core = 1.0 - smoothstep(-0.004, 0.004, d);
    // Falls off steeply enough to reach ~0 well inside the quad's own
    // bounds - otherwise the quad's straight edge shows up as a faint
    // rectangle wherever neighbouring pills' padding overlaps.
    float halo = exp(-max(d, 0.0) * 60.0) * (1.0 - core);

    float shimmer = 0.95 + 0.05 * sin(uTime * 1.8 + vSeed * 6.28318);
    vec3 hot = mix(uColor, vec3(1.0), 0.32);
    vec3 colour = mix(uColor, hot, vIntensity) * shimmer;

    float alpha = core * mix(uBaseOpacity, uGlowOpacity, vIntensity) + halo * uGlowOpacity * 0.2;
    if (alpha < 0.01) discard;
    gl_FragColor = vec4(colour, alpha);
  }
`;

const ringVertexShader = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const ringFragmentShader = /* glsl */ `
  uniform vec3 uColor;
  uniform float uOpacity;
  varying vec2 vUv;
  void main() {
    vec2 p = vUv - 0.5;
    float d = length(p);
    float ring = smoothstep(0.5, 0.44, d) * smoothstep(0.3, 0.36, d);
    if (ring < 0.02) discard;
    gl_FragColor = vec4(uColor, ring * uOpacity);
  }
`;

export function createPathTrail({ getSurfaceRadius }) {
  const group = new THREE.Group();

  const halfSpan = PILL_HALF_LENGTH + PILL_RADIUS;
  const pillGeometry = new THREE.PlaneGeometry(
    (halfSpan + GLOW_PADDING) * 2,
    (PILL_RADIUS + GLOW_PADDING) * 2,
    LENGTH_SEGMENTS,
    1
  );
  pillGeometry.setAttribute("aT", new THREE.InstancedBufferAttribute(new Float32Array(MAX_PILLS), 1));
  pillGeometry.setAttribute("aSeed", new THREE.InstancedBufferAttribute(new Float32Array(MAX_PILLS), 1));

  const pillMaterial = new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: 0 },
      uPulsePhase: { value: -1 },
      uPulseWidth: { value: PULSE_WIDTH },
      uHalfLength: { value: PILL_HALF_LENGTH },
      uRadius: { value: PILL_RADIUS },
      uHalfSpan: { value: halfSpan },
      uBendHeight: { value: BEND_HEIGHT },
      uBaseOpacity: { value: 0.38 },
      uGlowOpacity: { value: 0.68 },
      uColor: { value: BASE_COLOR },
    },
    vertexShader: pillVertexShader,
    fragmentShader: pillFragmentShader,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
  });

  const pillMesh = new THREE.InstancedMesh(pillGeometry, pillMaterial, MAX_PILLS);
  pillMesh.count = 0;
  pillMesh.frustumCulled = false;
  group.add(pillMesh);

  const ringGeometry = new THREE.PlaneGeometry(RING_OUTER_RADIUS * 2, RING_OUTER_RADIUS * 2);
  const ringMaterial = new THREE.ShaderMaterial({
    uniforms: { uColor: { value: TARGET_COLOR }, uOpacity: { value: 0.75 } },
    vertexShader: ringVertexShader,
    fragmentShader: ringFragmentShader,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const targetRing = new THREE.Mesh(ringGeometry, ringMaterial);
  targetRing.visible = false;
  targetRing.frustumCulled = false;
  group.add(targetRing);

  let pillCount = 0;
  let clock = 0;
  let ringSpinAngle = 0;
  const ringBaseQuat = new THREE.Quaternion();
  const _localZAxis = new THREE.Vector3(0, 0, 1);

  const _tempQuat = new THREE.Quaternion();
  const _tempScale = new THREE.Vector3(1, 1, 1);
  const _tempMatrix = new THREE.Matrix4();
  const _basisMatrix = new THREE.Matrix4();
  const _worldUp = new THREE.Vector3(0, 1, 0);

  function surfacePoint(direction) {
    const radius = getSurfaceRadius(direction);
    return direction.clone().multiplyScalar(radius + SURFACE_OFFSET);
  }

  function writeInstance(index, position, tangent, bitangent, direction, t) {
    _basisMatrix.makeBasis(tangent, bitangent, direction);
    _tempQuat.setFromRotationMatrix(_basisMatrix);
    pillMesh.setMatrixAt(index, _tempMatrix.compose(position, _tempQuat, _tempScale));
    pillGeometry.attributes.aT.setX(index, t);
    pillGeometry.attributes.aSeed.setX(index, Math.random());
  }

  function placeTargetRing(direction) {
    ringBaseQuat.setFromUnitVectors(_localZAxis, direction);
    targetRing.position.copy(surfacePoint(direction));
    ringSpinAngle = 0;
    targetRing.visible = true;
  }

  function setPath(directions) {
    if (!directions || directions.length === 0) {
      pillCount = 0;
      pillMesh.count = 0;
      targetRing.visible = false;
      return;
    }

    if (directions.length === 1) {
      pillCount = 0;
      pillMesh.count = 0;
      placeTargetRing(directions[0].clone().normalize());
      return;
    }

    const rawPoints = directions.map((dir) => surfacePoint(dir.clone().normalize()));

    let rawLength = 0;
    for (let i = 1; i < rawPoints.length; i++) rawLength += rawPoints[i].distanceTo(rawPoints[i - 1]);
    const avgSegment = rawLength / (rawPoints.length - 1);
    const epsilon = Math.max(0.02, avgSegment * 0.9);

    const simplified = simplifyPolyline(rawPoints, epsilon);
    const curve = new THREE.CatmullRomCurve3(simplified, false, "centripetal", 0.5);

    const totalLength = curve.getLength();
    const count = THREE.MathUtils.clamp(Math.round(totalLength / PILL_SPACING) + 1, 2, MAX_PILLS);
    const divisions = count - 1;
    const curvePoints = curve.getSpacedPoints(divisions);

    // The last sampled point is the destination - it gets the static ring,
    // not another pill, so pills only cover indices [0, divisions).
    const pillN = curvePoints.length - 1;
    for (let i = 0; i < pillN; i++) {
      const u = i / divisions;
      const rawTangent = curve.getTangentAt(u);
      const direction = curvePoints[i].clone().normalize();
      const position = surfacePoint(direction);

      let tangent = rawTangent.clone().addScaledVector(direction, -rawTangent.dot(direction));
      if (tangent.lengthSq() < 1e-6) tangent = new THREE.Vector3(1, 0, 0).cross(direction);
      tangent.normalize();
      const bitangent = new THREE.Vector3().crossVectors(direction, tangent).normalize();
      tangent.crossVectors(bitangent, direction).normalize();

      writeInstance(i, position, tangent, bitangent, direction, u);
    }
    pillCount = pillN;
    pillMesh.count = pillN;
    pillGeometry.attributes.aT.needsUpdate = true;
    pillGeometry.attributes.aSeed.needsUpdate = true;
    pillMesh.instanceMatrix.needsUpdate = true;

    placeTargetRing(curvePoints[divisions].clone().normalize());
  }

  function clear() {
    setPath([]);
  }

  function update(deltaTime) {
    clock += deltaTime;
    pillMaterial.uniforms.uTime.value = clock;
    if (pillCount > 0) {
      pillMaterial.uniforms.uPulsePhase.value = (clock * PULSE_SPEED) % PULSE_CYCLE;
    }
    if (targetRing.visible) {
      ringSpinAngle += deltaTime * RING_SPIN_SPEED;
      _tempQuat.setFromAxisAngle(_localZAxis, ringSpinAngle);
      targetRing.quaternion.copy(ringBaseQuat).multiply(_tempQuat);
    }
  }

  return { group, setPath, update, clear };
}
